import { useEffect, useRef } from "react";

const SPEECH_RMS_THRESHOLD = 0.02;
const MIN_SPEECH_MS = 250;
const SILENCE_HANGOVER_MS = 900;
const MAX_UTTERANCE_MS = 30_000;

interface VoiceActivityOptions {
  stream: MediaStream | null;
  /** Whether a detected speech-start/end should currently trigger a callback. */
  active: boolean;
  onSpeechStart: () => void;
  onSpeechEnd: () => void;
}

/**
 * Lightweight amplitude-based voice activity detector. Runs continuously
 * for as long as `stream` is open (tied to the mic stream's lifetime, not
 * to `active`) so the underlying AudioContext/AnalyserNode graph is built
 * once per hands-free session instead of being torn down and recreated on
 * every state change — important on iOS Safari, which is stingy about
 * AudioContext churn.
 */
export function useVoiceActivityDetector({
  stream,
  active,
  onSpeechStart,
  onSpeechEnd,
}: VoiceActivityOptions): void {
  const activeRef = useRef(active);
  activeRef.current = active;

  const onSpeechStartRef = useRef(onSpeechStart);
  onSpeechStartRef.current = onSpeechStart;

  const onSpeechEndRef = useRef(onSpeechEnd);
  onSpeechEndRef.current = onSpeechEnd;

  useEffect(() => {
    if (!stream) return;

    const AudioContextCtor =
      window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextCtor) return;

    const audioContext = new AudioContextCtor();
    void audioContext.resume().catch(() => {});

    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);

    const buffer = new Float32Array(analyser.fftSize);
    let speaking = false;
    let speechStartedAt = 0;
    let lastLoudAt = 0;
    let rafId = 0;
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;
      analyser.getFloatTimeDomainData(buffer);

      let sumSquares = 0;
      for (let i = 0; i < buffer.length; i++) sumSquares += buffer[i] * buffer[i];
      const rms = Math.sqrt(sumSquares / buffer.length);
      const now = performance.now();
      const isLoud = rms > SPEECH_RMS_THRESHOLD;
      if (isLoud) lastLoudAt = now;

      if (!activeRef.current) {
        speaking = false;
      } else if (!speaking) {
        if (isLoud) {
          speaking = true;
          speechStartedAt = now;
          onSpeechStartRef.current();
        }
      } else {
        const silentFor = now - lastLoudAt;
        const spokeFor = now - speechStartedAt;
        if (spokeFor > MIN_SPEECH_MS && (silentFor > SILENCE_HANGOVER_MS || spokeFor > MAX_UTTERANCE_MS)) {
          speaking = false;
          onSpeechEndRef.current();
        }
      }

      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close().catch(() => {});
    };
  }, [stream]);
}
